import { AtsExtraFieldTypeEnum } from '@/generated/graphql/types';
import { getCurrencySymbolByCode } from '@/shared/utils/currency';

// Returns the appropriate currency symbol based on field type and currency code
// Used for displaying currency values in extra fields
export const getCurrencySymbol = (
  fieldType: AtsExtraFieldTypeEnum,
  currency?: string | null,
): string => {
  if (fieldType === AtsExtraFieldTypeEnum.CurrencyField) {
    return getCurrencySymbolByCode(currency ?? undefined);
  }
  return '';
};

/**
 * Ensures that a URL value has https:// prefix (only if there is content)
 */
export const ensureHttps = (value: string): string => {
  if (!value || value.trim() === '') {
    return '';
  }
  const cleaned = value.replace(/^https?:\/\//i, '').trim();
  return cleaned ? `https://${cleaned}` : '';
};

/**
 * Processes the onChange event for LinkedIn URL input and returns the processed value
 */
export const processLinkedInOnChange = (inputValue: string): string => {
  // If the user empties the input or the input is only https://, return empty
  if (inputValue === '' || inputValue === 'https://') {
    return '';
  }
  // If the user deletes part of the https://, keep it
  if (inputValue.length < 8 || !inputValue.startsWith('https://')) {
    // If it starts with http but not https://, allow editing
    if (inputValue.startsWith('http')) {
      return inputValue;
    }
    // If it doesn't have https://, add it
    return ensureHttps(inputValue);
  }
  // Remove any duplicate https:// that the user pastes
  const cleaned = inputValue.replace(/^https?:\/\//i, '');
  return ensureHttps(cleaned);
};

/**
 * Processes the onBlur event for LinkedIn URL input and returns the processed value
 */
export const processLinkedInOnBlur = (inputValue: string): string => {
  // Ensure https:// when losing focus (only if there is content)
  if (inputValue === '' || inputValue === 'https://') {
    return '';
  }
  return ensureHttps(inputValue);
};

type TCountryCode = {
  value: string;
  iso?: string;
  label: string;
};

/**
 * Parses a phone value and extracts the country code and phone number
 */
export const parsePhoneValue = (
  value: string | undefined | null,
  countryCodes: TCountryCode[],
): { countryCode: string; phoneNumber: string } => {
  if (!value || !value.startsWith('+')) {
    return { countryCode: '', phoneNumber: '' };
  }
  const valueWithoutPlus = value.slice(1);
  let countryCode = '';
  let phoneNumber = valueWithoutPlus;

  const sortedCodes = [...countryCodes].sort(
    (a, b) => b.value.length - a.value.length,
  );
  for (const code of sortedCodes) {
    const codeWithoutPlus = code.value.replace('+', '');
    if (valueWithoutPlus.startsWith(codeWithoutPlus)) {
      countryCode = code.value; // Guardar con el "+" incluido
      phoneNumber = valueWithoutPlus.slice(codeWithoutPlus.length);
      break;
    }
  }

  return { countryCode, phoneNumber };
};

/**
 * Updates phone value by combining country code and phone number
 * Returns the formatted phone value string
 */
export const updatePhoneValue = (code: string, number: string): string => {
  if (code && number) {
    const codeWithoutPlus = code.replace('+', '');
    return `+${codeWithoutPlus}${number}`;
  }
  if (code) {
    const codeWithoutPlus = code.replace('+', '');
    return `+${codeWithoutPlus}`;
  }
  if (number) {
    return number;
  }
  return '';
};

/**
 * Returns the appropriate FormLabel className based on form type
 * Used for consistent styling between internal and external candidate forms
 */
export const getFormLabelClassName = (
  type: 'internal' | 'external',
): string => {
  return type === 'internal' ? 'tw-cds-font-subtitle-01' : 'tw-font-subtitle4';
};
